import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Calendar,
    User,
    Tag,
    ArrowLeft,
    ArrowUpRight,
    Share2,
    AlertCircle,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";
import { NewsArticle } from "@/types/news.types";
import { newsService } from "@/services/news.service";

export default function NewsDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [news, setNews] = useState<NewsArticle | null>(null);
    const [relatedNews, setRelatedNews] = useState<NewsArticle[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchNewsData = async () => {
            if (!slug) return;

            try {
                setLoading(true);
                setError(null);

                // Fetch the news article by slug
                const newsData = await newsService.getBySlug(slug);
                setNews(newsData);

                // For related news, we'd typically fetch from the same category
                // Since we don't have a category endpoint, we'll fetch all news and filter
                try {
                    const allNews = await newsService.getAllNews();
                    const related = allNews
                        .filter(n => n.category === newsData.category && n.id !== newsData.id)
                        .slice(0, 3);
                    setRelatedNews(related);
                } catch (err) {
                    // If fetching related news fails, just continue without them
                    console.warn("Could not fetch related news", err);
                }
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load news article"
                );
                console.error("Error fetching news:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchNewsData();
    }, [slug]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Loading state
    if (loading) {
        return (
            <div className="overflow-hidden">
                <section className="section-padding bg-cream text-center">
                    <div className="container-custom">
                        <div className="animate-pulse max-w-3xl mx-auto">
                            <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4" />
                            <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto" />
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // Error state
    if (error || !news) {
        return (
            <div className="overflow-hidden">
                <section className="section-padding bg-cream text-center">
                    <div className="container-custom">
                        <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                        <h1 className="text-gray-900 mb-4">
                            {error ? "Error Loading Article" : "Article Not Found"}
                        </h1>
                        <p className="text-gray-600 mb-8">
                            {error || "The news article you're looking for doesn't exist."}
                        </p>
                        {error && (
                            <button
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center space-x-2 bg-[#c9a84c] text-white px-6 py-3 rounded-lg hover:bg-[#b8973e] transition-colors mr-4"
                            >
                                <span>Retry</span>
                            </button>
                        )}
                        <Link
                            to="/news"
                            className="inline-flex items-center space-x-2 text-[#c9a84c] hover:text-[#b8973e] font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Back to News</span>
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="overflow-hidden">
            {/* Back Button */}
            <section className="section-padding bg-white border-b border-gray-100">
                <div className="container-custom">
                    <motion.button
                        whileHover={{ x: -4 }}
                        onClick={() => navigate("/news")}
                        className="inline-flex items-center space-x-2 text-[#c9a84c] hover:text-[#b8973e] font-medium transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to News</span>
                    </motion.button>
                </div>
            </section>

            {/* Hero Section */}
            <section className="relative py-12 md:py-20 bg-white">
                <div className="container-custom px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl mx-auto"
                    >
                        {/* Breadcrumb */}
                        <div className="flex items-center space-x-2 mb-6">
                            <Link
                                to="/news"
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                News
                            </Link>
                            <span className="text-gray-300">/</span>
                            <span className="text-sm text-[#c9a84c] font-medium">
                                {news.category}
                            </span>
                        </div>

                        {/* Title */}
                        <h1 className="text-4xl md:text-5xl font-display text-gray-900 mb-6 leading-tight">
                            {news.title}
                        </h1>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-gray-200">
                            <div className="flex items-center space-x-2 text-gray-600">
                                <User className="w-4 h-4 text-[#c9a84c]" />
                                <span className="text-sm">{news.author}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600">
                                <Calendar className="w-4 h-4 text-[#c9a84c]" />
                                <span className="text-sm">
                                    {formatDate(news.date)}
                                </span>
                            </div>

                            <div className="flex items-center space-x-2 text-gray-600">
                                <Tag className="w-4 h-4 text-[#c9a84c]" />
                                <span className="text-sm font-medium">
                                    {news.category}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Featured Image */}
            {news.image && (
                <section className="relative py-12 bg-white">
                    <div className="container-custom">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="max-w-3xl mx-auto"
                        >
                            <img
                                src={news.image}
                                alt={news.title}
                                loading="lazy"
                                className="w-full h-auto object-contain rounded-2xl shadow-lg"
                            />
                        </motion.div>
                    </div>
                </section>
            )}

            {/* News Content */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="prose prose-lg max-w-none"
                        >
                            <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm">
                                {/* Check if content is HTML or plain text */}
                                {news.content.includes("<") ? (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: news.content }}
                                        className="text-gray-700 leading-relaxed text-base md:text-lg prose-sm"
                                    />
                                ) : (
                                    news.content
                                        .split("\n\n")
                                        .map((paragraph, index) => (
                                            <p
                                                key={index}
                                                className="text-gray-700 leading-relaxed mb-6 last:mb-0 text-base md:text-lg"
                                            >
                                                {paragraph}
                                            </p>
                                        ))
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Share Section */}
            <section className="section-padding bg-white border-y border-gray-100">
                <div className="container-custom max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="flex items-center justify-between"
                    >
                        <h3 className="font-display text-lg text-gray-900">
                            Share this article
                        </h3>
                        <div className="flex items-center space-x-4">
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                title="Share"
                            >
                                <Share2 className="w-5 h-5 text-gray-600" />
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Related News */}
            {relatedNews.length > 0 && (
                <section className="section-padding bg-white">
                    <div className="container-custom">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            className="max-w-4xl mx-auto"
                        >
                            <h2 className="text-3xl font-display text-gray-900 mb-12 text-center">
                                Related News
                            </h2>

                            <StaggerContainer className="grid md:grid-cols-3 gap-6">
                                {relatedNews.map((relatedArticle) => (
                                    <StaggerItem key={relatedArticle.id}>
                                        <Link
                                            to={`/news/${relatedArticle.slug}`}
                                            className="group block h-full"
                                        >
                                            <motion.article
                                                whileHover={{ y: -6 }}
                                                className="glass-card-hover rounded-xl overflow-hidden h-full flex flex-col"
                                            >
                                                {relatedArticle.image && (
                                                    <div className="relative overflow-hidden">
                                                        <img
                                                            src={relatedArticle.image}
                                                            alt={relatedArticle.title}
                                                            loading="lazy"
                                                            className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                                        />
                                                    </div>
                                                )}

                                                <div className="p-4 flex-1 flex flex-col">
                                                    <span className="text-xs font-medium text-[#c9a84c] mb-2">
                                                        {relatedArticle.category}
                                                    </span>
                                                    <h4 className="font-display text-gray-900 mb-2 line-clamp-2 group-hover:text-[#c9a84c] transition-colors">
                                                        {relatedArticle.title}
                                                    </h4>
                                                    <p className="text-sm text-gray-600 flex-grow mb-3 line-clamp-2">
                                                        {relatedArticle.excerpt}
                                                    </p>
                                                    <div className="text-xs text-gray-500">
                                                        {formatDate(
                                                            relatedArticle.date
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.article>
                                        </Link>
                                    </StaggerItem>
                                ))}
                            </StaggerContainer>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="container-custom px-6 text-center">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Ready to Make Smart{" "}
                            <span className="gradient-text">
                                Financial Decisions
                            </span>
                            ?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Connect with our expert advisors for personalized
                            investment strategies
                        </p>
                        <Link to="/contact">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Get Expert Advice</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
